// onshape-selection-message.js — the PURE half of onshape-selection.js: which
// origin to trust, and what one Onshape SELECTION message says is selected.
// No React, no DOM, so test/onshape-selection.test.mjs runs it directly.
//
// The SELECTION payload shape was VERIFIED LIVE on 2026-09-24 — see the header of
// onshape-selection.js. Anything unrecognised still yields null.
const ONSHAPE_ORIGIN_RE = /^https:\/\/[\w-]+(\.[\w-]+)*\.onshape\.com$/;

// The origin to trust: Onshape's own `server` param when it is a valid Onshape
// origin, otherwise null (and the receive check falls back to the domain rule).
export function trustedOrigin(server) {
  const s = String(server || '').trim().replace(/\/+$/, '');
  return ONSHAPE_ORIGIN_RE.test(s) ? s : null;
}

export function originAllowed(origin, server) {
  const t = trustedOrigin(server);
  return t ? origin === t : ONSHAPE_ORIGIN_RE.test(String(origin || ''));
}

// selectionFromMessage(data) — one Onshape message → what is selected, or null
// when the message is not a selection we can read. Kinds:
//   none        nothing selected (a deselect)
//   body        exactly one whole part in a part studio → partId
//   occurrence  exactly one thing in an ASSEMBLY (it carries an occurrencePath)
//   entity      exactly one face / edge / vertex — names no part
//   multiple    more than one item
// A BODY's selectionId IS the part's deterministic id (the same id `{$partId}`
// substitutes), which is what makes the part-studio case resolvable at no cost.
export function selectionFromMessage(data) {
  if (!data || typeof data !== 'object') return null;
  if (!/^selection$/i.test(String(data.messageName || ''))) return null;
  const list = Array.isArray(data.selections) ? data.selections
    : Array.isArray(data.data?.selections) ? data.data.selections : null;
  if (!list) return null;
  if (list.length === 0) return { kind: 'none' };
  if (list.length > 1) return { kind: 'multiple', count: list.length };
  const s = list[0] || {};
  // ⚠ THE KIND LIVES IN `selectionType`, measured live 2026-09-24 (Alex, one part
  // and one composite part in a part studio):
  //   {"selections":[{"selectionType":"BODY","selectionId":"JJD",
  //     "workspaceMicroversionId":"fb70…"}],"messageName":"SELECTION"}
  // There is NO entityType. The first version read entityType only, so this exact
  // message — a whole part, with its id — was reported as "an unknown, not a whole
  // part". entityType / bodyType stay accepted in case another context sends them;
  // selectionType "ENTITY" is the generic word and says nothing about the kind.
  const st = String(s.selectionType || '').toUpperCase();
  const entityType = String(s.entityType || s.bodyType || (st && st !== 'ENTITY' ? st : '')).toUpperCase();
  const path = Array.isArray(s.occurrencePath) ? s.occurrencePath.filter(x => typeof x === 'string' && x) : [];
  if (path.length) return { kind: 'occurrence', path, entityType };
  const id = typeof s.selectionId === 'string' ? s.selectionId : typeof s.partId === 'string' ? s.partId : '';
  if (entityType === 'BODY' && id) return { kind: 'body', partId: id };
  return { kind: 'entity', entityType: entityType || 'unknown' };
}
